import * as Service from '@/App/Service';
import Vue, { VueConstructor } from 'vue';

/**
 * Vue Plugin for Feature Toggle System
 *
 * Provides the global `$feature` property to all Vue components, enabling
 * feature flag checks directly in templates without importing the Feature service.
 *
 * All calls delegate to `App/Service/Feature`, which checks override values
 * from `Config/Feature.ts` first, then falls back to store state (populated
 * by the /config/app API endpoint).
 *
 * Template usage:
 *
 *   <!-- Single feature check -->
 *   <div v-if="$feature('venue_map')">...</div>
 *
 *   <!-- All required -->
 *   <div v-if="$feature.all(['auth', 'auth_login'])">...</div>
 *
 *   <!-- Any of these -->
 *   <div v-if="$feature.any(['kit_pad', 'kit_trainer'])">...</div>
 *
 *   <!-- None of these -->
 *   <div v-if="$feature.none(['wallet', 'advertisements'])">...</div>
 *
 *   <!-- Feature + all dependencies enabled -->
 *   <div v-if="$feature.isFullyEnabled('auth_login')">...</div>
 *
 * @see App/Service/Feature.ts
 * @see Config/Feature.ts
 * @author Matt Kenefick <matt@polymermallard.com>
 * @package Plugin
 * @project ChalkySticks Web App
 */
const FeaturePlugin = {
	/**
	 * Install the plugin onto the Vue prototype
	 *
	 * @param VueConstructor _Vue
	 * @param any options
	 * @return void
	 */
	install(_Vue: VueConstructor, options?: any): void {
		// Create the $feature helper that can be called directly or has methods
		const featureHelper: IFeatureHelper = function (featureName: string): boolean {
			return Service.Feature.isEnabled(featureName);
		} as IFeatureHelper;

		// Add methods to the helper
		featureHelper.all = (featureNames: Array<string>): boolean => {
			return Service.Feature.hasAll(featureNames);
		};

		featureHelper.any = (featureNames: Array<string>): boolean => {
			return Service.Feature.hasAny(featureNames);
		};

		featureHelper.dependencies = (featureName: string): Array<string> => {
			return Service.Feature.getDependencies(featureName);
		};

		featureHelper.enabled = (featureName: string): boolean => {
			return Service.Feature.isEnabled(featureName);
		};

		featureHelper.getDependents = (featureName: string): Array<string> => {
			return Service.Feature.getDependents(featureName);
		};

		featureHelper.isFullyEnabled = (featureName: string): boolean => {
			return Service.Feature.isFullyEnabled(featureName);
		};

		featureHelper.none = (featureNames: Array<string>): boolean => {
			return Service.Feature.hasNone(featureNames);
		};

		// Define the property on the Vue prototype
		Object.defineProperty(_Vue.prototype, '$feature', {
			/**
			 * @return IFeatureHelper
			 */
			get(): IFeatureHelper {
				return featureHelper;
			},
		});
	},
};

// Module augmentation to add $feature to Vue instance type
declare module 'vue/types/vue' {
	interface Vue {
		$feature: IFeatureHelper;
	}
}

export default FeaturePlugin;
